import { t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import { UserRole } from '@repo/shared/types';
import { Button } from '@repo/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/card';
import { Plus, RefreshCw } from 'lucide-react';
import { parseAsInteger, parseAsString, parseAsStringEnum, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { OfficersTable } from '@/components/tables/officers-table';
import {
  type TOfficersTableFilters,
  ZOfficersTableFiltersSchema,
} from '@/components/tables/officers-table-filters';
import { trpc } from '@/providers/trpc';
import type { RouterOutputs } from '@/providers/trpc-client';

export function OfficersPage() {
  useLocale();

  const navigate = useNavigate();

  const [params, setParams] = useQueryStates(
    {
      page: parseAsInteger.withDefault(1),
      perPage: parseAsInteger.withDefault(10),
      search: parseAsString.withDefault(''),
      role: parseAsStringEnum([
        UserRole.ORG_OWNER,
        UserRole.ORG_MANAGER,
        UserRole.ORG_OFFICER,
        UserRole.ORG_KYB_OFFICER,
        UserRole.ORG_KYC_OFFICER,
        'ALL',
      ]).withDefault('ALL'),
    },
    {
      history: 'replace',
      shallow: false,
    },
  );
  const filters: TOfficersTableFilters = useMemo(() => {
    try {
      return ZOfficersTableFiltersSchema.parse(params);
    } catch {
      return ZOfficersTableFiltersSchema.parse({});
    }
  }, [getLocale(), params]);

  const handleFilterChange = useCallback(
    (key: keyof TOfficersTableFilters, value: string | undefined) => {
      setParams({
        [key]: value === undefined || value === '' ? null : value,
      });
    },
    [setParams],
  );

  const handleClearFilters = useCallback(() => {
    setParams({ search: '' });
  }, [setParams]);

  const {
    data: officersData,
    isFetching,
    isRefetching,
    isLoading: isOrganizationsLoading,
    refetch,
    isError,
    error,
  } = trpc.organization.listOrganizationMembers.useQuery({
    page: params.page,
    perPage: params.perPage,
    email: params.search,
    role: params.role && params.role !== 'ALL' ? params.role : undefined,
  });
  const isLoading = isFetching || isOrganizationsLoading || isRefetching;

  const officers: RouterOutputs['organization']['listOrganizationMembers'] = officersData ?? {
    data: [],
    perPage: 10,
    page: 1,
    totalPages: 1,
    total: 0,
  };
  return (
    <div className="flex-1 space-y-6 p-4 pt-6 md:p-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-bold text-3xl tracking-tight">{t('workspace.officers')}</h1>
          <p className="mt-1 text-muted-foreground">
            {t('auth.manageAndViewOrganizationEmployees')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => navigate('create')}>
            <Plus className="me-2 h-4 w-4" />
            {t('auth.addOfficer')}{' '}
          </Button>
          <Button disabled={isLoading} onClick={() => refetch()} variant="outline">
            <RefreshCw className={`me-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            {t('auth.refresh')}{' '}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('auth.allOfficers')}</CardTitle>
          <CardDescription>
            {isLoading
              ? t('auth.loadingOfficers')
              : t('count.officers', { count: officers.data.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <OfficersTable
            data={officers.data}
            error={error}
            filters={params}
            isError={isError}
            isLoading={isLoading}
            onClearFilters={handleClearFilters}
            onFilterChange={handleFilterChange}
            onRefetch={refetch}
          />
        </CardContent>
      </Card>
    </div>
  );
}
